import { useContext, useEffect, useMemo, useState } from "react";
import { Helmet } from "react-helmet-async";
import { Link, useNavigate, useParams } from "react-router-dom";
import Button from "../components/button";
import Carousel from "../components/carousel.jsx";
import ContentText from "../components/contentText.jsx";
import AdminPanel from "../components/machineAdminPanel";
import { ExtrasCarousel } from "../components/machineDetailsSection.jsx";
import { ImageLightbox } from "../components/machineImageGallery.jsx";
import MachineJsonLd from "../components/machineJsonLd.jsx";
import MarkdownDisplay from "../components/markdownDisplay";
import SectionContent from "../components/sectionContent.jsx";
import { MachineContext } from "../context/MachineContext";
import { UserContext } from "../context/UserContext";
import { useLocalePath } from "../hooks/useLocalePath";
import { uploadFile } from "../httpService";
import { resizeImageClient } from "../imageResize";
import {
  absoluteUrl,
  isNewMachine,
  machineMetaDescription,
  machineTitle,
  withBrand,
} from "../seoUtils";
import { machineCategoryUrlSlug } from "../slug.js";

const Machine = () => {
  const { id } = useParams();
  const { machines, machinesLoaded, reinitialize } = useContext(MachineContext);
  const { hasRole } = useContext(UserContext);
  const [fullImageToDisplay, setFullImageToDisplay] = useState(null);
  const [updatedMachine, setUpdatedMachine] = useState({
    price: 0,
    campaignPrice: 0,
    attributes: [],
    description: "",
    title: "",
    categorySlug: "",
    buildYear: "",
    manufacturer: "",
    images: [],
    videos: [],
    details: [],
    extras: [],
  });
  const navigate = useNavigate();
  const { locale, localePath } = useLocalePath();

  const machine = useMemo(() => machines.find((m) => m.id === Number(id)), [machines, id]);
  const heroImage = machine?.images?.[0] || null;
  const isNew = isNewMachine(machine);

  useEffect(() => {
    if (machine) {
      setUpdatedMachine({
        price: machine.price || 0,
        campaignPrice: machine.campaignPrice || 0,
        attributes: machine.attributes || [],
        description: machine.description || "",
        title: machine.title || "",
        categorySlug: machine.categorySlug || "",
        buildYear: machine.buildYear || "",
        manufacturer: machine.manufacturer || "",
        images: machine.images || [],
        videos: machine.videos || [],
        details: machine.details || [],
        extras: machine.extras || [],
      });
    }
  }, [machine]);

  const handleAttributeChange = (index, field, value) => {
    setUpdatedMachine((prev) => {
      const updatedAttributes = [...prev.attributes];
      updatedAttributes[index][field] = value;
      return { ...prev, attributes: updatedAttributes };
    });
  };

  const addAttribute = () => {
    setUpdatedMachine((prev) => {
      return {
        ...prev,
        attributes: [...prev.attributes, { key: "", value: "" }],
      };
    });
  };

  const removeAttribute = (index) => {
    setUpdatedMachine((prev) => {
      const updatedAttributes = [...prev.attributes];
      updatedAttributes.splice(index, 1);
      return { ...prev, attributes: updatedAttributes };
    });
  };

  const uploadImageFn = async (file) => {
    const fileToUpload = await resizeImageClient(file, {
      maxBytes: 1_048_576,
    });
    return uploadFile({
      file: fileToUpload,
    });
  };

  const addDetail = () =>
    setUpdatedMachine((prev) => ({
      ...prev,
      details: [...prev.details, { title: "", description: "", image: "" }],
    }));

  const removeDetail = (index) =>
    setUpdatedMachine((prev) => ({
      ...prev,
      details: prev.details.filter((_, i) => i !== index),
    }));

  const updateDetail = (index, field, value) =>
    setUpdatedMachine((prev) => {
      const updated = [...prev.details];
      updated[index] = { ...updated[index], [field]: value };
      return { ...prev, details: updated };
    });

  const addExtra = () =>
    setUpdatedMachine((prev) => ({
      ...prev,
      extras: [...prev.extras, { title: "", description: "", image: "" }],
    }));

  const removeExtra = (index) =>
    setUpdatedMachine((prev) => ({
      ...prev,
      extras: prev.extras.filter((_, i) => i !== index),
    }));

  const updateExtra = (index, field, value) =>
    setUpdatedMachine((prev) => {
      const updated = [...prev.extras];
      updated[index] = { ...updated[index], [field]: value };
      return { ...prev, extras: updated };
    });

  // As an element, never MachineNotFound(): called as a function its hooks would join this
  // component's list, and the machine list arriving mid-render changes the hook count (#310).
  //
  // machinesLoaded, not machines.length: the server inlines an empty list for a machine id that
  // does not exist, so this is the branch the server render has to take — otherwise the page it
  // answers 404 with is a blank document with an empty <title>.
  if (machinesLoaded && !machine) return <MachineNotFound />;

  // One name for the <h1>, the <title>, og:title and the Product JSON-LD — they used to
  // disagree, because half the titles carry the brand and half do not.
  const name = machine ? withBrand(machine) : "";
  const title = machine ? machineTitle(machine) : "";
  const description = machine ? machineMetaDescription(machine) : "";

  return (
    !!machine && (
      <>
        <Helmet>
          <title>{title}</title>
          <meta name="description" content={description} />
          <meta
            name="keywords"
            content={`AS Maskiner, ${machine.title}, ${isNew ? "ny" : "begagnad"} ${machine.category}, ${machine.manufacturer}, ${machine.manufacturer} ${machine.category}, ${isNew ? "nya" : "begagnade"} maskiner, CNC, kantlistmaskin, sprutautomat, fladderputs, bredbandputs, plattuppdelningssåg, listhyvel`}
          />

          {/* Open Graph — the same name and the same sentence as the title and description, so
              a shared link says exactly what the search result says. */}
          <meta property="og:url" content={absoluteUrl(locale, `/machines/${machine.id}`)} />
          <meta property="og:title" content={title} />
          <meta property="og:description" content={description} />
          <meta
            property="og:image"
            content={machine.images && machine.images.length > 0 ? machine.images[0] : ""}
          />
        </Helmet>

        {/* Structured data */}
        <MachineJsonLd machine={machine} />

        {/* 1. Hero — blue title + image straddling into gray, then attributes */}
        <div className="bg-[#004494] pb-16 sm:pb-0">
          {/* Title in blue area */}
          <div className="text-white pt-4 sm:pt-12 pb-3 sm:pb-6 px-4">
            <SectionContent>
              <h1 className="text-2xl sm:text-3xl md:text-4xl lg:text-5xl font-bold text-center">
                {name}
              </h1>
              <p className="text-center text-white/70 text-base sm:text-lg mt-1 sm:mt-2">
                {machine.categorySlug ? (
                  <Link
                    to={localePath(`/catalogue/${machineCategoryUrlSlug(machine)}`)}
                    className="text-white/70 hover:text-white underline-offset-4 hover:underline"
                  >
                    {machine.category}
                  </Link>
                ) : (
                  machine.category
                )}
              </p>
            </SectionContent>
          </div>

          {/* Image — bottom half pokes into the gray area below */}
          {heroImage && (
            <div className="flex justify-center px-4 -mb-48 relative z-10">
              <button
                type="button"
                className="cursor-pointer bg-transparent border-0 p-0"
                onClick={() => setFullImageToDisplay(heroImage)}
                aria-label={`Visa fullbild av ${machine.title}`}
              >
                <img
                  src={heroImage}
                  alt={machine.title}
                  className="max-w-3xl w-full object-contain drop-shadow-2xl rounded"
                  style={{ maxHeight: "calc(60vh)" }}
                />
              </button>
            </div>
          )}
        </div>

        {/* Attributes in gray area — pt-28 compensates for the image overflow */}
        {machine.attributes && machine.attributes.length > 0 && (
          <div className={`bg-gray-50 ${heroImage ? "pt-36 sm:pt-52" : "pt-8"} pb-16 px-4`}>
            <SectionContent>
              <div className="grid grid-cols-2 md:grid-cols-3 gap-x-8 gap-y-6">
                {machine.attributes.map((attr, i) => (
                  <div key={i} className="text-center py-4">
                    <p className="text-2xl font-bold text-gray-900">{attr.value}</p>
                    <p className="text-sm text-gray-500 mt-1">{attr.key}</p>
                  </div>
                ))}
              </div>
            </SectionContent>
          </div>
        )}

        {/* 3. Details carousel */}
        {machine.details && machine.details.length > 0 && (
          <section className="bg-white py-16" data-testid="details-carousel">
            <Carousel
              lightBackground
              items={machine.details.map((d) => ({
                imageUrl: d.image,
                title: d.title,
                subtitle: d.description,
              }))}
            />
          </section>
        )}

        {/* 4. First video only */}
        {machine.videos && machine.videos.length > 0 && (
          <section
            className="bg-black flex items-center justify-center"
            data-testid="video-section"
          >
            {/* biome-ignore lint/a11y/useMediaCaption: user-uploaded video */}
            <video
              src={machine.videos[0]}
              controls
              className="w-full block"
              style={{ maxHeight: "calc(100vh - 4rem)" }}
            />
          </section>
        )}

        {/* 5. Extras section — carousel layout like details */}
        {machine.extras && machine.extras.length > 0 && <ExtrasCarousel extras={machine.extras} />}

        {/* 6. Extra section — markdown content with headings, paragraphs, images */}
        {machine.description && (
          <section className="py-12 bg-white">
            <SectionContent>
              <MarkdownDisplay data={machine.description} />
            </SectionContent>
          </section>
        )}

        {/* 6. Sticky CTA buttons */}
        <div className="fixed bottom-24 right-4 z-40 flex flex-col gap-3" data-testid="sticky-cta">
          <Link
            to={localePath(`/machines/${machine.id}/quotation`)}
            data-testid={`machine-${machine.id}-quotation-link`}
          >
            <Button sx="shadow-lg w-full">
              <ContentText contentKey="machine.cta_quote" fallback="Begär offert" />
            </Button>
          </Link>
        </div>

        {/* Admin panel */}
        {hasRole("admin") && (
          <AdminPanel
            machine={machine}
            updatedMachine={updatedMachine}
            setUpdatedMachine={setUpdatedMachine}
            reinitialize={reinitialize}
            navigate={navigate}
            localePath={localePath}
            uploadImageFn={uploadImageFn}
            handleAttributeChange={handleAttributeChange}
            addAttribute={addAttribute}
            removeAttribute={removeAttribute}
            addDetail={addDetail}
            removeDetail={removeDetail}
            updateDetail={updateDetail}
            addExtra={addExtra}
            removeExtra={removeExtra}
            updateExtra={updateExtra}
          />
        )}
        <ImageLightbox
          imageToDisplay={fullImageToDisplay}
          machineTitle={machine.title}
          onClose={() => setFullImageToDisplay(null)}
        />
      </>
    )
  );
};

const MachineNotFound = () => {
  const { localePath } = useLocalePath();
  return (
    <div className="flex flex-col items-center justify-center h-[75vh] p-4 max-w-2xl mx-auto">
      {/* The page says 404 and carries no machine — it has a name of its own and must not be
          indexed, exactly like the category and manufacturer not-found screens. */}
      <Helmet>
        <title>Maskinen finns inte | AS Maskiner</title>
        <meta name="robots" content="noindex" />
      </Helmet>
      <div className="shadow-lg p-8 w-full">
        <h1 className="text-2xl sm:text-3xl md:text-4xl font-bold mb-4 text-center text-secondary">
          Ojdå, den här maskinen finns inte!
        </h1>
        <div className="text-base sm:text-base md:text-base text-center">
          <p className="mb-8 text-text-body">
            Detta kan bero på att maskinen du letar efter redan blivit såld, eller att den utgått
            från vårat utbud. Om du tror att detta är ett fel, vänligen{" "}
            <Link
              to={localePath("/contact")}
              className="underline text-primary hover:text-info transition-colors duration-300"
            >
              kontakta oss
            </Link>
            .
          </p>
          <div>
            <Link
              to={localePath("/catalogue")}
              className="inline-block bg-primary text-inverse py-2 px-6 rounded-md shadow-md hover:bg-primary800 transition-colors duration-300"
            >
              Sök bland våra andra maskiner
            </Link>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Machine;
